<template>
    <BaseDialog v-model:open="isReviewDialogOpen" :title="displayTitle" dialog-content-class="md:w-130 pb-0!">
        <div class="flex flex-col w-full">
            <div class="flex w-full gap-2 items-center justify-between px-6 pt-5">
                <h2 class="title-body sm:title-subsection grow">{{ displayTitle }}</h2>
                <BaseButton theme="neutral" class="border-none" aria-label="Close" @click="isReviewDialogOpen = false">
                    <i class="bi bi-x-lg text-lg" aria-hidden="true"></i>
                </BaseButton>
            </div>
            <HorizontalDivider />
            <!-- Connections  -->
            <div class="w-full flex flex-col pt-1">
                <!-- Search input -->
                <div class="px-6 pb-5">
                    <div class="relative flex items-center">
                        <i class="bi bi-search text-lg/none text-valence-grey-750 absolute left-3 pointer-events-none" aria-hidden="true"></i>
                        <input
                            ref="searchInput"
                            v-model="connectionSearchQuery"
                            type="text"
                            aria-label="Search your connections"
                            placeholder="Search your connections"
                            class="p-2 pl-9 pr-8 rounded-lgr border border-valence-grey-300 w-full" />
                        <button
                            v-if="connectionSearchQuery"
                            type="button"
                            aria-label="Clear search"
                            class="absolute right-3 text-valence-grey-750 hover:text-valence-grey-900"
                            @click="connectionSearchQuery = ''">
                            <i class="bi bi-x-lg text-sm" aria-hidden="true"></i>
                        </button>
                    </div>
                </div>
                <!-- Connection list — on mobile capped to ~3.5 rows (4th peeks to signal scrollability) so the Cancel/Save
                     footer stays visible without scrolling the dialog; on desktop the taller ~5+ row view is preserved. -->
                <div class="flex flex-col px-6 max-h-56 md:max-h-80 overflow-y-auto pb-5">
                    <div v-if="hasNoMatches" class="body-small text-valence-grey-500 px-3 py-3">No people found — you can add them below.</div>
                    <TransitionGroup name="connection-list" tag="div" class="flex flex-col">
                        <button
                            v-for="connection in alphabeticalConnections"
                            :key="connection.id"
                            tabindex="0"
                            class="flex items-center text-start px-3 py-2.5 mb-1 cursor-pointer select-none rounded-lg transition-colors"
                            :class="connectionState[connection.id]?.checked ? 'bg-valence-primary-200/50' : 'hover:bg-valence-grey-50'"
                            @click="toggleConnection(connection.id)"
                            @keydown.enter="toggleConnection(connection.id)"
                            @keydown.space.prevent="toggleConnection(connection.id)">
                            <div
                                class="bg-valence-primary-200 text-valence-primary-600 flex items-center justify-center w-9 h-9 body-small-bold rounded-full shrink-0"
                                translate="no">
                                {{ getFirstLastInitials(connection.persona.name ?? "") }}
                            </div>
                            <div class="flex flex-col ml-3 grow">
                                <div class="body-bold">{{ connection.persona.name }}</div>
                                <div class="body-small text-valence-grey-750">{{ connection.job_title }}</div>
                            </div>
                            <div class="shrink-0 ml-3" @click.stop>
                                <BaseCheckbox
                                    :model-value="!!connectionState[connection.id]?.checked"
                                    shape="circle"
                                    @update:model-value="(val) => setConnectionState(connection.id, 'checked', val)" />
                            </div>
                        </button>
                    </TransitionGroup>
                </div>
                <!-- Add someone new -->
                <div class="px-6 pb-4">
                    <HorizontalDivider class="mt-0! mb-4!" />
                    <div class="rounded-xl p-3 transition-colors" :class="hasNoMatches ? 'bg-valence-primary-200/30 ring-1 ring-valence-primary-300' : ''">
                        <div class="body-small-bold mb-3">Add someone new</div>
                        <div class="flex flex-col sm:flex-row gap-3 sm:items-center">
                            <input
                                v-model="addConnectionForm.name"
                                type="text"
                                class="rounded-lgr border border-valence-grey-300 px-3 py-2 text-sm grow w-full min-w-0"
                                aria-label="Name"
                                placeholder="Name" />
                            <input
                                v-model="addConnectionForm.jobTitle"
                                type="text"
                                class="rounded-lgr border border-valence-grey-300 px-3 py-2 text-sm grow w-full min-w-0"
                                aria-label="Title"
                                placeholder="Title" />
                            <button
                                type="button"
                                class="rounded-lgr border border-valence-grey-300 px-4 py-2 text-sm font-medium hover:bg-valence-grey-50 whitespace-nowrap shrink-0 w-full sm:w-auto"
                                :disabled="!addConnectionForm.name.trim()"
                                @click="createConnection">
                                + Add
                            </button>
                        </div>
                    </div>
                </div>
                <!-- Footer buttons -->
                <HorizontalDivider class="my-0!" />
                <div class="flex justify-end gap-3 px-6 py-4">
                    <BaseButton theme="neutral" @click="isReviewDialogOpen = false">Cancel</BaseButton>
                    <BaseButton theme="primary" :disabled="isReviewSelectionSubmitting" class="flex gap-2 items-center" @click="handleReviewConfirm">
                        <LoadingSpinner v-if="isReviewSelectionSubmitting" class="h-4" /> Save
                    </BaseButton>
                </div>
            </div>
        </div>
        <BaseToast v-model:open="isErrorToastOpen" theme="error" :description="toastErrorMessage" />
    </BaseDialog>
</template>

<script setup lang="ts">
import { useDebounceFn } from "@vueuse/core";
import { useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseToast from "~vue/components/BaseToast.vue";
import BaseCheckbox from "~vue/components/form/BaseCheckbox.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import { usePerformanceReviewRelationships } from "~vue/Moments/composables/usePerformanceReviewRelationships";
import { PERFORMANCE_REVIEW_EVENT_PREFIX } from "~vue/Moments/constants";
import { logUserInteraction } from "~vue/utils/logUtils";
import { isSessionEndedError } from "~vue/utils/sessionEnded";
import { getFirstLastInitials } from "~vue/utils/stringUtils";
import { computed, nextTick, onUnmounted, reactive, ref, watch } from "vue";

import { useConnectionsSearch } from "./composables/useConnectionsSearch";

const { title = "Add team members" } = defineProps<{ title?: string }>();

const isReviewDialogOpen = defineModel("dialog", {
    type: Boolean,
    required: true,
});

const searchInput = ref<HTMLInputElement>();

watch(isReviewDialogOpen, (open) => {
    if (open) {
        connectionSearchQuery.value = "";
        // Discard any unsaved toggles from a previous (canceled) session by resetting
        // the working selection back to the last saved state before showing the dialog.
        connectionState.value = Object.fromEntries(Object.entries(initialConnectionState.value).map(([id, state]) => [id, { ...state }]));
        addConnectionForm.name = "";
        addConnectionForm.jobTitle = "";
        autoFilledName.value = "";
        nextTick(() => searchInput.value?.focus());
    }
});

const connectionState = ref<Record<string, Record<string, unknown>>>({});
const initialConnectionState = ref<Record<string, Record<string, unknown>>>({});

const setConnectionState = (id: string, key: string, value: unknown) => {
    connectionState.value[id] = {
        ...connectionState.value[id],
        [key]: value,
    };
};

const addConnectionForm = reactive({
    name: "",
    jobTitle: "",
});

const toggleConnection = (id: string) => {
    const currentChecked = !!connectionState.value[id]?.checked;
    setConnectionState(id, "checked", !currentChecked);
};

// Show "Edit team" once a team already exists; otherwise this is the initial "Add team members" flow.
const displayTitle = computed(() => (Object.keys(initialConnectionState.value).length > 0 ? "Edit team" : title));

const connectionSearchQuery = ref("");

const createConnectionPayload = computed(() => ({
    job_title: addConnectionForm.jobTitle,
    persona: {
        name: addConnectionForm.name.trim(),
    },
}));

const isErrorToastOpen = ref(false);
const toastErrorMessage = ref("");
const {
    data: createdConnection,
    error: createConnectionError,
    execute: createConnectionExecute,
} = useFetch("/api/connections/", { immediate: false }).post(createConnectionPayload).json();

const createConnection = async () => {
    try {
        await createConnectionExecute();
        if (createConnectionError.value) {
            throw createConnectionError.value;
        }
        connectionState.value[createdConnection.value.id] = {
            ...connectionState.value[createdConnection.value.id],
            checked: true,
        };
        addConnectionForm.name = "";
        addConnectionForm.jobTitle = "";
        logUserInteraction("connection_added", { source: "performance_review" });
    } catch (error) {
        if (!isSessionEndedError(error)) {
            isErrorToastOpen.value = true;
            toastErrorMessage.value = "Failed to create connection. Please try again.";
        }
    }

    try {
        await fetchConnections();
    } catch {
        isErrorToastOpen.value = true;
        toastErrorMessage.value = "Failed to refresh connections. Please try again.";
    }
};

const { connections, searchQuery, fetchConnections } = useConnectionsSearch();

const alphabeticalConnections = computed(() => {
    if (!connections.value) {
        return [];
    }
    // Selected people float to the top (matches prototype), then alphabetical within each group.
    return [...connections.value].sort((a, b) => {
        const aChecked = !!connectionState.value[a.id]?.checked;
        const bChecked = !!connectionState.value[b.id]?.checked;
        if (aChecked !== bChecked) {
            return aChecked ? -1 : 1;
        }
        return (a.persona.name ?? "").localeCompare(b.persona.name ?? "");
    });
});

// When a search returns no matches, guide the user toward adding a new person:
// surface a message, highlight the "Add someone new" section, and keep the name
// field in sync with the search box as they type.
// Note: we deliberately don't gate on the fetching state — useFetch keeps the previous
// results during a refetch, so reading them directly stays stable instead of blinking
// false→true on every debounced search.
const hasNoMatches = computed(() => !!connectionSearchQuery.value.trim() && alphabeticalConnections.value.length === 0);

// Tracks the value we auto-filled so we can keep mirroring the search while the user
// types, but stop the moment they edit the name themselves.
const autoFilledName = ref("");

watch([connectionSearchQuery, hasNoMatches], () => {
    const query = connectionSearchQuery.value.trim();
    if (hasNoMatches.value) {
        // Mirror the search into the name on every keystroke, unless the user has
        // taken over the field (name diverged from what we last auto-filled).
        if (!addConnectionForm.name || addConnectionForm.name === autoFilledName.value) {
            addConnectionForm.name = query;
            autoFilledName.value = query;
        }
    } else if (addConnectionForm.name && addConnectionForm.name === autoFilledName.value) {
        // Matches reappeared (or the search was cleared) and the name was auto-filled — clear it.
        addConnectionForm.name = "";
        autoFilledName.value = "";
    }
});

// Fetch existing performance review relationships
const { data: performanceReviewData, execute: fetchPerformanceReviewRelationships } = usePerformanceReviewRelationships();

watch(
    performanceReviewData,
    (data) => {
        if (data?.relationships) {
            const initialState: Record<string, Record<string, unknown>> = {};
            data.relationships.forEach((relationship) => {
                initialState[relationship.reviewee_id] = { checked: true };
                setConnectionState(relationship.reviewee_id, "checked", true);
            });
            initialConnectionState.value = initialState;
        }
    },
    { immediate: true },
);

const debouncedSearchQuery = useDebounceFn(() => {
    searchQuery.value = connectionSearchQuery.value;
}, 500);

watch(connectionSearchQuery, () => {
    debouncedSearchQuery();
});

const reviewPayload = computed(() => ({
    relationships: Object.entries(connectionState.value)
        .filter(([, state]) => state.checked)
        .map(([connectionId]) => ({ reviewee_id: connectionId })),
}));

const { execute: bulkUpdateReviewSelection, isFetching: isReviewSelectionSubmitting } = useFetch("/api/coach/performance_reviews/relationships/bulk/", { immediate: false })
    .post(reviewPayload)
    .json();

const handleReviewConfirm = async () => {
    const allConnectionIds = new Set([...Object.keys(initialConnectionState.value), ...Object.keys(connectionState.value)]);

    allConnectionIds.forEach((connectionId) => {
        const initialState = initialConnectionState.value[connectionId];
        const currentState = connectionState.value[connectionId];

        const wasChecked = initialState?.checked === true;
        const isChecked = currentState?.checked === true;

        if (!wasChecked && isChecked) {
            logUserInteraction(`${PERFORMANCE_REVIEW_EVENT_PREFIX}relationship_added`);
        } else if (wasChecked && !isChecked) {
            logUserInteraction(`${PERFORMANCE_REVIEW_EVENT_PREFIX}relationship_removed`);
        }
    });

    try {
        await bulkUpdateReviewSelection();
        await fetchPerformanceReviewRelationships();
        isReviewDialogOpen.value = false;
    } catch {
        isErrorToastOpen.value = true;
        toastErrorMessage.value = "Failed to submit reviews. Please try again.";
    }
};

onUnmounted(() => {
    connectionSearchQuery.value = "";
});
</script>

<style scoped>
:deep() {
    .check {
        color: #555ba2;
    }
}

.connection-list-enter-active,
.connection-list-leave-active {
    transition: all 0.2s ease;
}

.connection-list-enter-from,
.connection-list-leave-to {
    opacity: 0;
}
</style>
